/**
 * Chat: Verlauf aus der DB (chat_buffer) als Startwert, danach live über das
 * WS-Signal `live.chat`. Senden via game.sayAll / game.sayTeam.
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, activeServer, live, type ChatRecord } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

function hhmmss(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toTimeString().slice(0, 8);
}
const quote = (s: string) => `"${s.replace(/"/g, "")}"`;

export function Chat() {
  const msg = useSignal("");
  const team = useSignal("1");
  const edit = useSignal(false);

  async function loadHistory() {
    const sv = activeServer.value;
    if (!sv) return;
    const r = await api.call<ApiResponse & { messages: ChatRecord[] }>("chat_buffer", { server: sv });
    if (r.ok) live.chat.value = r.messages;
  }
  useSignalEffect(() => { if (activeServer.value) void loadHistory(); });

  async function sayAll() {
    const sv = activeServer.value;
    const m = msg.value.trim();
    if (!sv || !m) return;
    const r = await api.call<ApiResponse>("rcon", { server: sv, cmd: `game.sayAll ${quote(m)}` });
    if (r.ok) { toast(t("chat.broadcastSent"), "ok"); msg.value = ""; } else toast(String(r.error ?? t("common.error")), "err");
  }
  async function sayTeam() {
    const sv = activeServer.value;
    const m = msg.value.trim();
    if (!sv || !m) return;
    const r = await api.call<ApiResponse>("rcon", { server: sv, cmd: `game.sayTeam ${team.value} ${quote(m)}` });
    if (r.ok) { toast(`${t("chat.sentToTeam")} ${team.value}`, "ok"); msg.value = ""; } else toast(String(r.error ?? t("common.error")), "err");
  }

  if (!activeServer.value) return (<><PageHead title={t("nav.chat")} /><NoServer /></>);

  const lines = live.chat.value;
  const items: PanelItem[] = [{
    id: "chat", title: t("chat.history"), defaultSpan: 12,
    render: () => (
      <Panel
        title={t("chat.history")}
        actions={<span class="badge"><span class={`live-dot ${live.players.value.length || lines.length ? "open" : ""}`} /> {t("common.live")}</span>}
      >
        <div style="display:flex;flex-direction:column;gap:6px;max-height:52vh;overflow:auto;font-size:13.5px">
          {lines.length === 0 ? (
            <div class="empty">{t("chat.empty")}</div>
          ) : (
            lines.map((l) => (
              <div key={l.id} class="row" style="gap:var(--s2);align-items:baseline">
                <span class="mono faint" style="font-size:12px">{hhmmss(l.ts)}</span>
                <span class="badge" style="height:18px;padding:0 7px;font-size:10px">{l.tag}</span>
                <span style="font-weight:600">{l.sender}</span>
                <span class="muted">{l.message}</span>
              </div>
            ))
          )}
        </div>
        <div class="row" style="margin-top:var(--s4)">
          <input
            class="input grow"
            placeholder={t("chat.messagePlaceholder")}
            value={msg.value}
            onInput={(e) => (msg.value = (e.target as HTMLInputElement).value)}
            onKeyDown={(e) => { if (e.key === "Enter") void sayAll(); }}
          />
          <select class="select" style="width:auto" value={team.value} onChange={(e) => (team.value = (e.target as HTMLSelectElement).value)}>
            <option value="1">PAC</option>
            <option value="2">EU</option>
          </select>
          <button class="btn" onClick={() => void sayTeam()}>{t("chat.toTeam")}</button>
          <button class="btn primary" onClick={() => void sayAll()}>{t("chat.toAll")}</button>
        </div>
      </Panel>
    ),
  }];

  return (
    <div class="view-enter">
      <PageHead title={t("nav.chat")} sub={t("chat.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
      </PageHead>
      <PanelGrid view="chat" items={items} edit={edit} />
    </div>
  );
}
